// Centralized icon set. Every UI icon renders a Heroicon (@heroicons/react),
// wrapped to stay drop-in compatible with the previous MUI SvgIcon usage:
//   - supports sx, fontSize and color props (width/height track fontSize
//     the same way MUI sizes icons, and currentColor follows the color prop);
//   - emits data-testid="<Name>Icon" to match MUI's convention.
// Heroicons ships no brand logos, so the social icons map to the closest generic
// Heroicon, and Circle is a plain filled dot Heroicons does not ship.
import { forwardRef } from 'react';
import { styled } from '@mui/material/styles';
import {
  ArrowDownTrayIcon,
  ArrowLeftIcon,
  ArrowPathIcon,
  ArrowPathRoundedSquareIcon,
  ArrowRightIcon,
  ArrowRightStartOnRectangleIcon,
  ArrowTopRightOnSquareIcon,
  ArrowTrendingUpIcon,
  ArrowsPointingOutIcon,
  BanknotesIcon,
  Bars3Icon,
  BellAlertIcon,
  BellIcon,
  BookOpenIcon,
  BriefcaseIcon,
  BuildingOffice2Icon,
  BuildingStorefrontIcon,
  CalendarDaysIcon,
  CameraIcon,
  ChartBarIcon,
  ChatBubbleBottomCenterTextIcon,
  ChartBarSquareIcon,
  CheckCircleIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronUpIcon,
  ClockIcon,
  CloudArrowUpIcon,
  Cog6ToothIcon,
  ComputerDesktopIcon,
  CreditCardIcon,
  CurrencyDollarIcon,
  CursorArrowRaysIcon,
  DevicePhoneMobileIcon,
  DocumentChartBarIcon,
  DocumentDuplicateIcon,
  DocumentTextIcon,
  EllipsisHorizontalCircleIcon,
  EllipsisVerticalIcon,
  EnvelopeIcon,
  EnvelopeOpenIcon,
  ExclamationCircleIcon,
  EyeIcon,
  EyeSlashIcon,
  FireIcon,
  FolderIcon,
  FunnelIcon,
  GlobeAltIcon,
  HashtagIcon,
  InformationCircleIcon,
  KeyIcon,
  LanguageIcon,
  LightBulbIcon,
  LinkIcon,
  ListBulletIcon,
  LockClosedIcon,
  MagnifyingGlassIcon,
  MapPinIcon,
  MinusIcon,
  PauseCircleIcon,
  PencilSquareIcon,
  PhoneIcon,
  PhotoIcon,
  PlusCircleIcon,
  PlusIcon,
  PresentationChartLineIcon,
  PrinterIcon,
  QrCodeIcon,
  ReceiptPercentIcon,
  ReceiptRefundIcon,
  RocketLaunchIcon,
  ScissorsIcon,
  ShieldCheckIcon,
  ShoppingBagIcon,
  ShoppingCartIcon,
  SparklesIcon,
  Squares2X2Icon,
  StarIcon,
  StopIcon,
  SwatchIcon,
  TableCellsIcon,
  TagIcon,
  TrashIcon,
  TruckIcon,
  TvIcon,
  UserCircleIcon,
  UserGroupIcon,
  UserIcon,
  UsersIcon,
  ViewColumnsIcon,
  ViewfinderCircleIcon,
  WalletIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import {
  BoltIcon as BoltSolidIcon,
  CheckCircleIcon as CheckCircleSolidIcon,
  ExclamationCircleIcon as ExclamationCircleSolidIcon,
  ExclamationTriangleIcon as ExclamationTriangleSolidIcon,
  StarIcon as StarSolidIcon,
  XCircleIcon as XCircleSolidIcon,
} from '@heroicons/react/24/solid';

const FONT_SIZE = {
  inherit: 'inherit',
  small: '1.25rem',
  medium: '1.5rem',
  large: '2.1875rem',
};

const resolveColor = (theme, color) => {
  if (!color || color === 'inherit' || color === 'default') return 'inherit';
  if (color === 'action') return theme.palette.action.active;
  if (color === 'disabled') return theme.palette.action.disabled;
  return theme.palette[color]?.main ?? color;
};

const styleIcon = (HeroIcon) =>
  styled(HeroIcon, {
    shouldForwardProp: (prop) => prop !== 'fontSize' && prop !== 'color',
  })(({ theme, fontSize = 'medium', color = 'inherit' }) => ({
    width: '1em',
    height: '1em',
    flexShrink: 0,
    userSelect: 'none',
    fontSize: FONT_SIZE[fontSize] ?? fontSize,
    color: resolveColor(theme, color),
  }));

const adapt = (HeroIcon, name) => {
  const StyledIcon = styleIcon(HeroIcon);
  const Icon = forwardRef(function Icon(props, ref) {
    return <StyledIcon ref={ref} data-testid={`${name}Icon`} {...props} />;
  });
  Icon.displayName = `${name}Icon`;
  return Icon;
};

// Plain filled dot used for status indicators; Heroicons has no equivalent.
const DotIcon = forwardRef(function DotIcon(props, ref) {
  return (
    <svg ref={ref} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
      <circle cx="12" cy="12" r="8" />
    </svg>
  );
});

export const AccessTime = adapt(ClockIcon, 'AccessTime');
export const AccountCircle = adapt(UserCircleIcon, 'AccountCircle');
export const Add = adapt(PlusIcon, 'Add');
export const AllInclusive = adapt(ArrowPathRoundedSquareIcon, 'AllInclusive');
export const Analytics = adapt(ChartBarSquareIcon, 'Analytics');
export const ArrowBack = adapt(ArrowLeftIcon, 'ArrowBack');
export const ArrowForward = adapt(ArrowRightIcon, 'ArrowForward');
export const AttachMoney = adapt(CurrencyDollarIcon, 'AttachMoney');
export const BarChart = adapt(ChartBarIcon, 'BarChart');
export const Bolt = adapt(BoltSolidIcon, 'Bolt');
export const Business = adapt(BuildingOffice2Icon, 'Business');
export const Cancel = adapt(XCircleSolidIcon, 'Cancel');
export const Check = adapt(CheckIcon, 'Check');
export const CheckBox = adapt(CheckCircleSolidIcon, 'CheckBox');
export const CheckBoxOutlineBlank = adapt(StopIcon, 'CheckBoxOutlineBlank');
export const CheckCircle = adapt(CheckCircleSolidIcon, 'CheckCircle');
export const ChevronLeft = adapt(ChevronLeftIcon, 'ChevronLeft');
export const Close = adapt(XMarkIcon, 'Close');
export const CloudUpload = adapt(CloudArrowUpIcon, 'CloudUpload');
export const ContentCopy = adapt(DocumentDuplicateIcon, 'ContentCopy');
export const CreditCard = adapt(CreditCardIcon, 'CreditCard');
export const Dashboard = adapt(Squares2X2Icon, 'Dashboard');
export const Delete = adapt(TrashIcon, 'Delete');
export const DeliveryDining = adapt(TruckIcon, 'DeliveryDining');
export const Devices = adapt(ComputerDesktopIcon, 'Devices');
export const Edit = adapt(PencilSquareIcon, 'Edit');
export const Email = adapt(EnvelopeIcon, 'Email');
export const Error = adapt(ExclamationCircleSolidIcon, 'Error');
export const ErrorOutline = adapt(ExclamationCircleIcon, 'ErrorOutline');
export const ExitToApp = adapt(ArrowRightStartOnRectangleIcon, 'ExitToApp');
export const ExpandLess = adapt(ChevronUpIcon, 'ExpandLess');
export const ExpandMore = adapt(ChevronDownIcon, 'ExpandMore');
export const Group = adapt(UserGroupIcon, 'Group');
export const Groups = adapt(UsersIcon, 'Groups');
export const History = adapt(ClockIcon, 'History');
export const InfoOutlined = adapt(InformationCircleIcon, 'InfoOutlined');
export const Insights = adapt(PresentationChartLineIcon, 'Insights');
export const KeyboardArrowDown = adapt(ChevronDownIcon, 'KeyboardArrowDown');
export const Language = adapt(LanguageIcon, 'Language');
export const Launch = adapt(ArrowTopRightOnSquareIcon, 'Launch');
export const LocalDining = adapt(FireIcon, 'LocalDining');
export const LocationOn = adapt(MapPinIcon, 'LocationOn');
export const Lock = adapt(LockClosedIcon, 'Lock');
export const LockReset = adapt(KeyIcon, 'LockReset');
export const MailOutline = adapt(EnvelopeIcon, 'MailOutline');
export const MarkEmailRead = adapt(EnvelopeOpenIcon, 'MarkEmailRead');
export const Menu = adapt(Bars3Icon, 'Menu');
export const MenuBook = adapt(BookOpenIcon, 'MenuBook');
export const Notes = adapt(DocumentTextIcon, 'Notes');
export const Notifications = adapt(BellIcon, 'Notifications');
export const NotificationsActive = adapt(BellAlertIcon, 'NotificationsActive');
export const PauseCircleOutline = adapt(PauseCircleIcon, 'PauseCircleOutline');
export const Payment = adapt(CreditCardIcon, 'Payment');
export const Payments = adapt(BanknotesIcon, 'Payments');
export const Pending = adapt(EllipsisHorizontalCircleIcon, 'Pending');
export const Person = adapt(UserIcon, 'Person');
export const Phone = adapt(PhoneIcon, 'Phone');
export const PhoneAndroid = adapt(DevicePhoneMobileIcon, 'PhoneAndroid');
export const PhoneIphone = adapt(DevicePhoneMobileIcon, 'PhoneIphone');
export const Print = adapt(PrinterIcon, 'Print');
export const QrCode = adapt(QrCodeIcon, 'QrCode');
export const Receipt = adapt(ReceiptPercentIcon, 'Receipt');
export const ReceiptLong = adapt(ReceiptPercentIcon, 'ReceiptLong');
export const Refresh = adapt(ArrowPathIcon, 'Refresh');
export const Remove = adapt(MinusIcon, 'Remove');
export const Replay = adapt(ArrowPathIcon, 'Replay');
export const Restaurant = adapt(BuildingStorefrontIcon, 'Restaurant');
export const RestaurantMenu = adapt(BookOpenIcon, 'RestaurantMenu');
export const RocketLaunch = adapt(RocketLaunchIcon, 'RocketLaunch');
export const Save = adapt(ArrowDownTrayIcon, 'Save');
export const SaveAlt = adapt(ArrowDownTrayIcon, 'SaveAlt');
export const Search = adapt(MagnifyingGlassIcon, 'Search');
export const Security = adapt(ShieldCheckIcon, 'Security');
export const Settings = adapt(Cog6ToothIcon, 'Settings');
export const ShoppingCart = adapt(ShoppingCartIcon, 'ShoppingCart');
export const Star = adapt(StarSolidIcon, 'Star');
export const Store = adapt(BuildingStorefrontIcon, 'Store');
export const Storefront = adapt(BuildingStorefrontIcon, 'Storefront');
export const TableBar = adapt(TableCellsIcon, 'TableBar');
export const TableRestaurant = adapt(TableCellsIcon, 'TableRestaurant');
export const TakeoutDining = adapt(ShoppingBagIcon, 'TakeoutDining');
export const Timer = adapt(ClockIcon, 'Timer');
export const TimerOutlined = adapt(ClockIcon, 'TimerOutlined');
export const TouchApp = adapt(CursorArrowRaysIcon, 'TouchApp');
export const TrackChanges = adapt(ViewfinderCircleIcon, 'TrackChanges');
export const TrendingUp = adapt(ArrowTrendingUpIcon, 'TrendingUp');
export const Visibility = adapt(EyeIcon, 'Visibility');
export const VisibilityOff = adapt(EyeSlashIcon, 'VisibilityOff');
export const WarningAmber = adapt(ExclamationTriangleSolidIcon, 'WarningAmber');
export const CalendarToday = adapt(CalendarDaysIcon, 'CalendarToday');
export const Clear = adapt(XMarkIcon, 'Clear');
export const FilterList = adapt(FunnelIcon, 'FilterList');
export const Preview = adapt(EyeIcon, 'Preview');
export const Crop = adapt(ScissorsIcon, 'Crop');
export const PhotoCamera = adapt(CameraIcon, 'PhotoCamera');
export const Image = adapt(PhotoIcon, 'Image');
export const Info = adapt(InformationCircleIcon, 'Info');
export const Update = adapt(ArrowPathIcon, 'Update');
export const LocalOffer = adapt(TagIcon, 'LocalOffer');
export const AccountBalanceWallet = adapt(WalletIcon, 'AccountBalanceWallet');
export const AddCircleOutline = adapt(PlusCircleIcon, 'AddCircleOutline');
export const CheckCircleOutline = adapt(CheckCircleIcon, 'CheckCircleOutline');
export const Schedule = adapt(ClockIcon, 'Schedule');
export const TaskAlt = adapt(CheckCircleIcon, 'TaskAlt');
export const DragIndicator = adapt(EllipsisVerticalIcon, 'DragIndicator');
export const Link = adapt(LinkIcon, 'Link');
export const Folder = adapt(FolderIcon, 'Folder');
export const Assessment = adapt(DocumentChartBarIcon, 'Assessment');
export const AutoAwesome = adapt(SparklesIcon, 'AutoAwesome');
export const Autorenew = adapt(ArrowPathIcon, 'Autorenew');
export const StarBorder = adapt(StarIcon, 'StarBorder');
export const Tv = adapt(TvIcon, 'Tv');
export const ViewKanban = adapt(ViewColumnsIcon, 'ViewKanban');
export const ViewList = adapt(ListBulletIcon, 'ViewList');
export const ViewModule = adapt(Squares2X2Icon, 'ViewModule');
export const MoneyOff = adapt(ReceiptRefundIcon, 'MoneyOff');
export const Download = adapt(ArrowDownTrayIcon, 'Download');
export const OpenWith = adapt(ArrowsPointingOutIcon, 'OpenWith');
export const SelectAll = adapt(Squares2X2Icon, 'SelectAll');
export const Warning = adapt(ExclamationTriangleSolidIcon, 'Warning');
export const OpenInNew = adapt(ArrowTopRightOnSquareIcon, 'OpenInNew');
export const Palette = adapt(SwatchIcon, 'Palette');
export const TextFields = adapt(DocumentTextIcon, 'TextFields');
export const LocalShipping = adapt(TruckIcon, 'LocalShipping');
export const QrCode2 = adapt(QrCodeIcon, 'QrCode2');
export const ChatBubble = adapt(ChatBubbleBottomCenterTextIcon, 'ChatBubble');
export const Lightbulb = adapt(LightBulbIcon, 'Lightbulb');
export const Circle = adapt(DotIcon, 'Circle');

// Social links: Heroicons has no brand logos, so each maps to the closest
// generic Heroicon (camera for photos, briefcase for professional, etc.).
export const Facebook = adapt(GlobeAltIcon, 'Facebook');
export const Instagram = adapt(CameraIcon, 'Instagram');
export const Twitter = adapt(HashtagIcon, 'Twitter');
export const LinkedIn = adapt(BriefcaseIcon, 'LinkedIn');

// Brand logo mark (not a Heroicon — see Logo.js).
export { default as Logo, LOGO_BRAND_COLOR } from './Logo';
